import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener, NgZone,
  OnInit,
} from '@angular/core';
import {Observable} from 'rxjs';
import {filter, switchMap, take, tap} from 'rxjs/operators';

import {AppComponent} from '../../app.component';
import {isBigEventOptimization, isRemoveSidebar, MAP_ROTATION_ALLOWED} from 'src/configs/view_only';
import {CesiumAdapterService} from '../../services/cesiumAdapter21/cesium-adapter.service';
import {ItemManagerService} from '../../services/itemManager/item-manager.service';
import {ServerConnectionService} from '../../services/serverConnection/server-connection.service';
import {OverlayService} from '../../services/overlay/overlay.service';
import {LoggerService} from '../../../logger/logger.service';
import {CalculateInputsService} from '../../services/calculateInputsService/calculate-inputs.service';
import {KeyListenersService} from '../../services/keyListenersService/key-listeners.service';
import {EsBsModalService} from '../../services/bsModalService/bs-modal.service';
import {CookieService} from '../../services/coockieService/coockie.service';
import {TimelineService} from '../../services/timeline/timeline.service';
import {UiAdapterService} from '../../services/uiAdapterService/ui-adapter.service';
import {MyPositionService} from '../../services/myPosition/my-position.service';
import {DxfImportService} from '../../services/dxfImport/dxf-import.service';
import {DynamicScriptLoaderService} from '../../services/scriptService/DynamicScriptLoaderService';
import {UserGuideService} from '@modules/user-guide/shared/user-guide.service';
import {GroupService} from '../../services/groupService/group-service.service';
import {MouseListenersService} from '@src/app/services/mouseListeners/mouse-listeners.service';
import {ToolsService} from '@src/app/services/toolsService/tools.service';
import {StoryService} from '@src/app/services/storyService/story.service';
import {EventService} from '@src/app/core/services/event.service';
import {ModalService} from '@src/app/core/services/modal.service';
import {LinkShareAccessNudgeModalComponent} from '../link-share-access-nudge-modal/link-share-access-nudge-modal.component';
import {ClientService} from '@src/app/core/services/client.service';
import {PaywallUserService} from '@modules/paywall/services/paywallUserService/paywall-user.service';

declare var jQuery;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent21 extends AppComponent implements OnInit {
  isRemoveSidebar = isRemoveSidebar;
  slidesState = 'closed';
  isUserGuideEnabled: boolean;
  isModalOpen: boolean;
  isBigEventOptimization = isBigEventOptimization;

  constructor(
    protected dynamicScriptLoader: DynamicScriptLoaderService,
    protected cesiumAdapterService: CesiumAdapterService,
    protected itemManager: ItemManagerService,
    protected serverConnection: ServerConnectionService,
    protected overlayService: OverlayService,
    protected logger: LoggerService,
    protected calculateItemInputsService: CalculateInputsService,
    protected cd: ChangeDetectorRef,
    protected kl: KeyListenersService,
    protected modalControl: EsBsModalService,
    protected cookieService: CookieService,
    protected timelineService: TimelineService,
    protected uiAdapterService: UiAdapterService,
    protected myPosition: MyPositionService,
    protected dxfImport: DxfImportService,
    protected userGuideService: UserGuideService,
    protected gs: GroupService,
    protected toolsService: ToolsService,
    protected storyService: StoryService,
    protected eventService: EventService,
    protected modalService: ModalService,
    protected clientService: ClientService,
    protected paywallS: PaywallUserService,
    protected ngZone: NgZone
  ) {
    super(
      dynamicScriptLoader,
      cesiumAdapterService,
      itemManager,
      serverConnection,
      overlayService,
      logger,
      calculateItemInputsService,
      cd,
      kl,
      modalControl,
      cookieService,
      uiAdapterService,
      userGuideService,
      gs,
      toolsService,
      storyService,
      timelineService,
      paywallS,
      ngZone
    );

    this.myPosition.initialize();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.isUserGuideEnabled = this.userGuideService.isEnabled;

    if (this.userGuideService.isFastSignup && !this.userGuideService.isPlaceSet()) {
      this.userGuideService.showLoseEventProgressWarning = true;

      if (this.userGuideService.getFastLineEventDetails().location) {
        this.userGuideService.getLatLngForEvent().subscribe(geometry => {
          // Focus the map to the location entered within the FastLine form
          this.cesiumAdapterService.render({
            lat: geometry.lat,
            lng: geometry.lng,
          });
        });

        return;
      }

      // Lat/lng is provided when a location isn't
      const params = new URLSearchParams(window.location.search);
      const lat: number = +params.get('lat');
      const lng: number = +params.get('lng');

      // Sets the event location
      this.userGuideService.getAddressFromLatLng(lat, lng).subscribe();
      this.cesiumAdapterService.render({lat, lng});

      // Draw a pin on the map indicating specifically where those lat/lng coords lie
      this.cesiumAdapterService.viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(lng, lat, 1),
        billboard: {
          image: '/images/maps-pin.png',
          width: 20,
          height: 35,
        },
        label: {
          text: this.userGuideService.getFastLineEventDetails().name,
          font: '24px sans-serif',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -25),
        },
      });
    }

    this.modalService.isOpen$.subscribe((isOpen: boolean) => {
      // Have this here for onPush change detection
      this.isModalOpen = isOpen;
      this.cd.detectChanges();
    });

    if (this.eventService.isLinkShare && !this.eventService.hasPermission && this.clientService.id) {
      const secondsBeforeAccessNudge = !!this.clientService.id
        ? 2000
        : 1000 * 60 * 3;

      setTimeout(() => {
        this.modalService.open(LinkShareAccessNudgeModalComponent, {
          width: '300px',
        });

        this.cd.detectChanges();
      }, secondsBeforeAccessNudge);
    }
  }

  isTimelineEnabled(): boolean {
    return this.timelineService.isEnabled();
  }

  isMapRotationAllowed(): boolean {
    return MAP_ROTATION_ALLOWED && (!this.isBigEventOptimization || this.gs.itemsLoadedVal)
  }
}
